import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { MensagemService } from '../services/mensagem.service';

/**
 * Liga o toast de erro central para uma requisição.
 *
 * Opt-in por enquanto: só as chamadas marcadas usam o tratamento central,
 * para migrar tela a tela sem afetar as que ainda tratam o erro localmente.
 * Quando todas as telas estiverem migradas, o padrão pode ser invertido para
 * opt-out (liga por default; `context` desliga onde houver tratamento próprio).
 */
export const SHOW_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/** Marca uma requisição HttpClient para exibir o toast de erro central. */
export function comToastDeErro(context: HttpContext = new HttpContext()): HttpContext {
  return context.set(SHOW_ERROR_TOAST, true);
}

export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

export function semToastDeErro(context: HttpContext = new HttpContext()): HttpContext {
  return context.set(SUPPRESS_ERROR_TOAST, true);
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const mensagens = inject(MensagemService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (req.context.get(SUPPRESS_ERROR_TOAST) || !deveNotificar(error, req.context.get(SHOW_ERROR_TOAST))) {
        return throwError(() => error);
      }

      mensagens.add({ severity: 'error', summary: 'Atenção', detail: extrairMensagem(error), life: 4000 });

      return new Observable<never>(subscriber => {
        mensagens.comErrosLocaisSuprimidos(() => subscriber.error(error));
      });
    }),
  );
};

function deveNotificar(error: HttpErrorResponse, optIn: boolean): boolean {
  if (error.status === 401) return false;

  return optIn || error.status === 403;
}

/** Extrai a mensagem em português mais específica que o backend fornecer. */
function extrairMensagem(error: HttpErrorResponse): string {
  if (error.status === 0) {
    return 'Falha de conexão. Verifique sua internet e tente novamente.';
  }

  const body = error.error as { message?: string; errors?: Record<string, string[]> } | null;

  const primeiroErro = body?.errors ? Object.values(body.errors)[0]?.[0] : undefined;
  if (primeiroErro) return primeiroErro;

  if (error.status < 500 && typeof body?.message === 'string' && ehMensagemDeUsuario(body.message)) {
    return body.message;
  }

  if (error.status === 403) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  return 'Erro ao processar a solicitação. Tente novamente.';
}

function ehMensagemDeUsuario(mensagem: string): boolean {
  return mensagem.trim() !== '' && !/^(This action is unauthorized|Unauthenticated|Server Error|Forbidden|Unauthorized)\.?$/i.test(mensagem.trim());
}
